import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import colors from '../../../../../../config/theme/colors';
import { getAllGenerators, getAllLeads } from 'Routes/Dbs/utils';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TEpilepsyESDbsTreatmentType } from '../../definitions/epilepsy';
import { ClearIconStyled } from '..';
import { Container, Item } from 'Components/Grid';

const dbsLeadOptions = getAllLeads('epilepsy');

const DBSTreatmentField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const current: TEpilepsyESDbsTreatmentType =
    searchFields.epilepsyDbsTreatment !== 'noCropping' ? searchFields.epilepsyDbsTreatment : {};
  const status = current.status ?? [];

  const onChangeStatus = (values: TOnChangeValues) => {
    const newStatus = (values['status'] as Array<'ongoing' | 'ended'>) ?? [];
    if (newStatus.length === 0) {
      onChange({ epilepsyDbsTreatment: 'noCropping' });
    } else {
      onChange({ epilepsyDbsTreatment: { ...current, status: newStatus } });
    }
  };

  const onChangeLead = (values: TOnChangeValues) => {
    const newLead = values['lead'] as string | null;
    onChange({ epilepsyDbsTreatment: { ...current, lead: newLead ?? undefined } });
  };

  const onChangeGenerator = (values: TOnChangeValues) => {
    onChange({ epilepsyDbsTreatment: { ...current, generator: (values['generator'] as string) ?? undefined } });
  };

  const leadOptionFormatter = (name: string | number): string => {
    const n = `${name}`;
    if (n === 'Medtronic SenSight (0.5 mm Spaced Lead Assembly)')
      return formatMessage({ id: 'dbs.medtronicSenSightLead0.5mm' });
    if (n === 'Medtronic SenSight (1.5 mm Spaced Lead Assembly)')
      return formatMessage({ id: 'dbs.medtronicSenSightLead1.5mm' });
    return n;
  };

  const generatorOptions = getAllGenerators('epilepsy');

  return (
    <React.Fragment>
      <InputHandler
        type="Checkbox"
        editing={true}
        name="status"
        formData={{
          onChange: onChangeStatus,
          document: { status },
        }}
        options={['ongoing', 'ended']}
        optionFormatter={(name: string | number) => fm(`extendedSearch.opts.${name}`)}
      />
      {status.length > 0 && (
        <React.Fragment>
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 0.5rem 0' }}>
            {fm('extendedSearch.lead')}
          </div>
          <Container style={{ alignItems: 'center' }}>
            <Item>
              <InputHandler
                type="Select"
                editing={true}
                name="lead"
                formData={{
                  onChange: onChangeLead,
                  document: { lead: current.lead ?? null },
                }}
                options={dbsLeadOptions}
                optionFormatter={leadOptionFormatter}
                placeholder="dbs.chooseLead"
                fittedWidth
              />
            </Item>
            {current.lead && (
              <Item style={{ marginLeft: '2rem' }}>
                <ClearIconStyled onClick={() => onChange({ epilepsyDbsTreatment: { ...current, lead: undefined } })} />
              </Item>
            )}
          </Container>
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 0.5rem 0' }}>
            {fm('extendedSearch.generator')}
          </div>
          <Container style={{ alignItems: 'center' }}>
            <Item>
              <InputHandler
                type="SelectByCategory"
                editing={true}
                name="generator"
                formData={{
                  onChange: onChangeGenerator,
                  document: { generator: current.generator ?? '' },
                }}
                options={generatorOptions}
                optionFormatter={(name: string | number): string => `${name}`}
                placeholder="dbs.chooseGenerator"
                formatTitle={true}
              />
            </Item>
            {current.generator && (
              <Item style={{ marginLeft: '2rem' }}>
                <ClearIconStyled
                  onClick={() => onChange({ epilepsyDbsTreatment: { ...current, generator: undefined } })}
                />
              </Item>
            )}
          </Container>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default DBSTreatmentField;
